import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  ReferenceLine,
} from 'recharts';
import { Box, Typography, Paper, Chip, Stack } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { PeriodTrend } from '../../services/analyticsService';

interface TrendChartProps {
  data: PeriodTrend[];
  title: string;
  type?: 'line' | 'area';
}

const TrendChart: React.FC<TrendChartProps> = ({ data, title, type = 'line' }) => {
  // Formatear datos para el gráfico con fechas mejoradas
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return data.map((item) => {
      // Validar que periodo existe
      if (!item.period) {
        return {
          periodo: '',
          periodoFormateado: 'N/A',
          promedio: 0,
          auditorias: 0,
        };
      }
      
      // Parsear la fecha como string para evitar problemas de timezone
      const [year, month, day] = item.period.split('-');
      const fecha = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      const mes = fecha.toLocaleDateString('es-ES', { month: 'short' });
      const dia = fecha.getDate();
      
      return {
        periodo: item.period,
        periodoFormateado: `${dia} ${mes}`,
        promedio: parseFloat(item.avg_compliance || '0'),
        auditorias: parseInt(item.total_audits || '0'),
      };
    });
  }, [data]);

  // Calcular estadísticas
  const stats = useMemo(() => {
    if (chartData.length === 0) return null;

    const promedios = chartData.map(d => d.promedio).filter(p => p > 0);
    const promedioGeneral = promedios.reduce((a, b) => a + b, 0) / promedios.length;
    const mejorDia = chartData.reduce((max, item) => item.promedio > max.promedio ? item : max);
    const peorDia = chartData.reduce((min, item) => item.promedio > 0 && item.promedio < min.promedio ? item : min);
    
    // Calcular tendencia (comparar primera mitad vs segunda mitad)
    const mitad = Math.floor(promedios.length / 2);
    const primerasMitad = promedios.slice(0, mitad);
    const segundaMitad = promedios.slice(mitad);
    const promedioPrimera = primerasMitad.reduce((a, b) => a + b, 0) / primerasMitad.length;
    const promedioSegunda = segundaMitad.reduce((a, b) => a + b, 0) / segundaMitad.length;
    const cambio = promedioSegunda - promedioPrimera;

    return {
      promedio: promedioGeneral,
      mejor: mejorDia,
      peor: peorDia,
      tendencia: cambio > 2 ? 'up' : cambio < -2 ? 'down' : 'flat',
      cambio: cambio
    };
  }, [chartData]);

  // Componente de tooltip personalizado mejorado
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" fontWeight="bold" gutterBottom color="text.primary">
            📅 {data.periodoFormateado}
          </Typography>
          <Typography variant="h6" color="primary" gutterBottom>
            {data.promedio.toFixed(1)}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data.auditorias} auditoría{data.auditorias !== 1 ? 's' : ''}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box>
      {/* Header con título y estadísticas */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          {stats && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                icon={stats.tendencia === 'up' ? <TrendingUp /> : stats.tendencia === 'down' ? <TrendingDown /> : <TrendingFlat />}
                label={`${stats.tendencia === 'up' ? '↗' : stats.tendencia === 'down' ? '↘' : '→'} ${stats.cambio > 0 ? '+' : ''}${stats.cambio.toFixed(1)}%`}
                size="small"
                color={stats.tendencia === 'up' ? 'success' : stats.tendencia === 'down' ? 'error' : 'default'}
                sx={{ fontWeight: 'bold' }}
              />
              <Chip
                label={`Promedio: ${stats.promedio.toFixed(1)}%`}
                size="small"
                variant="outlined"
              />
            </Stack>
          )}
        </Box>
        {stats && (
          <Stack direction="row" spacing={1}>
            <Chip
              label={`🏆 Mejor: ${stats.mejor.promedio.toFixed(1)}%`}
              size="small"
              color="success"
              variant="outlined"
            />
            <Chip
              label={`📉 Mínimo: ${stats.peor.promedio.toFixed(1)}%`}
              size="small"
              color="warning"
              variant="outlined"
            />
          </Stack>
        )}
      </Box>

      {/* Gráfico */}
      <ResponsiveContainer width="100%" height={280}>
        {type === 'area' ? (
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorPromedio" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#1976d2" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="periodoFormateado"
              style={{ fontSize: '12px' }}
              angle={-45}
              textAnchor="end"
              height={80}
              stroke="#666"
            />
            <YAxis
              domain={[0, 100]}
              style={{ fontSize: '12px' }}
              stroke="#666"
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Línea de promedio para referencia visual */}
            {stats && (
              <ReferenceLine
                y={stats.promedio}
                stroke="#9e9e9e"
                strokeDasharray="3 3"
                strokeWidth={1.5}
                label={{ 
                  value: `Promedio del período: ${stats.promedio.toFixed(1)}%`, 
                  position: 'top', 
                  fill: '#616161', 
                  fontSize: 11,
                  fontWeight: 500
                }}
              />
            )}
            
            <Area
              type="monotone"
              dataKey="promedio"
              stroke="#1976d2"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorPromedio)"
              name="Cumplimiento"
              dot={{ r: 4, fill: '#1976d2', strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 6, fill: '#1976d2', strokeWidth: 3, stroke: '#fff' }}
            />
          </AreaChart>
        ) : (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="periodoFormateado"
              style={{ fontSize: '12px' }}
              angle={-45}
              textAnchor="end"
              height={80}
              stroke="#666"
            />
            <YAxis
              yAxisId="left"
              domain={[0, 100]}
              style={{ fontSize: '12px' }}
              label={{ value: 'Cumplimiento (%)', angle: -90, position: 'insideLeft' }}
              stroke="#666"
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              style={{ fontSize: '12px' }}
              label={{ value: 'Auditorías', angle: 90, position: 'insideRight' }}
              stroke="#666"
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: '14px', paddingTop: '20px' }} />
            
            {/* Línea de promedio para referencia visual */}
            {stats && (
              <ReferenceLine
                yAxisId="left"
                y={stats.promedio}
                stroke="#9e9e9e"
                strokeDasharray="3 3"
                strokeWidth={1.5}
                label={{ 
                  value: `Promedio del período: ${stats.promedio.toFixed(1)}%`, 
                  position: 'top', 
                  fill: '#616161', 
                  fontSize: 11,
                  fontWeight: 500
                }}
              />
            )}
            
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="promedio"
              stroke="#1976d2"
              strokeWidth={3}
              dot={{ r: 4, fill: '#1976d2', strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 6, fill: '#1976d2', strokeWidth: 3, stroke: '#fff' }}
              name="Cumplimiento"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="auditorias"
              stroke="#82ca9d"
              strokeWidth={2}
              dot={{ r: 3, fill: '#82ca9d' }}
              activeDot={{ r: 5, fill: '#82ca9d' }}
              name="Total Auditorías"
            />
          </LineChart>
        )}
      </ResponsiveContainer>
    </Box>
  );
};

export default TrendChart;
